import { useEffect, useState, useCallback } from "react";
import { Head, KV } from "./shared";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RefreshCw, ShieldAlert, Trash2, Archive, RotateCcw, Eye, EyeOff, KeyRound, Check } from "lucide-react";

export function SettingsView({ user }) {
  return (
    <div>
      <Head title="Settings" desc="Account and platform configuration." />
      <div className="bg-navy-800 border border-white/5 p-6 max-w-lg space-y-4">
        <KV k="Name" v={user?.name} /><KV k="Email" v={user?.email} /><KV k="Role" v={user?.role} />
        <div className="pt-4 border-t border-white/10 text-sm text-gray-500">AI provider and Stripe keys are configured via environment variables. The AI layer is provider-agnostic (OpenAI GPT-5.5 by default).</div>
      </div>

      <ChangePassword />
      {user?.is_owner && <ProposalQrSettings />}
      {user?.is_owner && <VeteranDiscountSettings />}
      {user?.is_owner && <PurgeOrphans />}
      {user?.is_owner && <StartFresh />}
    </div>
  );
}

function PasswordField({ label, value, onChange, testid, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">{label}</label>
      <div className="relative">
        <input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={testid} autoComplete={autoComplete}
          className="w-full bg-navy border border-white/20 text-white px-3 py-2.5 pr-11 text-sm focus:border-gold focus:outline-none" />
        <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${testid}-toggle`}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors">
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;
  const canSubmit = cur && next.length >= 8 && next === confirm && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setDone(false);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next, confirm_password: confirm });
      setCur(""); setNext(""); setConfirm(""); setDone(true);
      toast.success("Your password has been updated.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-8 border border-white/10 bg-navy-800 max-w-lg" data-testid="change-password-panel">
      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-white/10">
        <KeyRound className="text-gold" size={18} />
        <div>
          <div className="font-heading text-white text-lg">Change password</div>
          <div className="text-xs text-gray-400">Set your own admin password. Use the eye icon to double-check what you typed. It stays set across restarts and redeploys.</div>
        </div>
      </div>
      <div className="p-6 space-y-4">
        <PasswordField label="Current password" value={cur} onChange={setCur} testid="cp-current" autoComplete="current-password" />
        <PasswordField label="New password" value={next} onChange={setNext} testid="cp-new" autoComplete="new-password" />
        <PasswordField label="Confirm new password" value={confirm} onChange={setConfirm} testid="cp-confirm" autoComplete="new-password" />
        {tooShort && <p className="text-xs text-amber-400" data-testid="cp-too-short">New password must be at least 8 characters.</p>}
        {mismatch && <p className="text-xs text-red-400" data-testid="cp-mismatch">New password and confirmation don't match.</p>}
        {done && <p className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid="cp-success"><Check size={13} /> Password updated. Use it next time you sign in.</p>}
        <button onClick={submit} disabled={!canSubmit} data-testid="cp-submit"
          className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <KeyRound size={14} />} Update password
        </button>
      </div>
    </div>
  );
}

function ProposalQrSettings() {
  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());

  useEffect(() => {
    api.get("/admin/proposal-settings")
      .then((r) => { setUrl(r.data.qr_url || ""); setEnabled(r.data.qr_enabled !== false); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/proposal-settings", { qr_url: url.trim(), qr_enabled: enabled });
      setBust(Date.now());
      toast.success("Proposal QR settings saved.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const qrSrc = `${process.env.REACT_APP_BACKEND_URL}/api/proposal-qr.png?t=${bust}`;

  return (
    <div className="mt-8 border border-white/10 bg-navy-800 max-w-3xl" data-testid="proposal-qr-settings">
      <div className="px-6 py-4 border-b border-white/10">
        <div className="font-heading text-white text-lg">Proposal QR code</div>
        <div className="text-xs text-gray-400">A scannable code is added to every proposal PDF and online view. Point it at your booking / assessment page or a QR-app "hub" link (use a dynamic link so scans are tracked). Leave blank to default to your AI Business Assessment.</div>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="text-gray-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex-1 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">QR destination URL</label>
                <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="proposal-qr-url"
                  placeholder="https://qr.atfwebworks.com/hub  (or leave blank for /start)"
                  className="w-full bg-navy border border-white/20 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="proposal-qr-enabled" className="accent-gold" />
                Show the QR code on proposals
              </label>
              <button onClick={save} disabled={busy} data-testid="proposal-qr-save"
                className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin" size={14} /> : null} Save
              </button>
            </div>
            <div className="text-center">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Preview</div>
              <img src={qrSrc} alt="Proposal QR preview" className="w-32 h-32 bg-white p-2 rounded mx-auto"
                onError={(e) => { e.currentTarget.style.opacity = "0.2"; }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const SCOPE_OPTS = [
  ["all", "All eligible offers"],
  ["packages", "Website packages (deposit & balance)"],
  ["plan_unlock", "Business Plan PDF unlock ($99)"],
  ["template", "Website design purchases"],
  ["concierge_bundle", "AI Concierge bundle"],
];

function VeteranDiscountSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/veteran-discount")
      .then((r) => setCfg({ ...r.data, scope: r.data.scope || ["all"] }))
      .catch(() => setCfg({ enabled: true, code: "SALUTE15", percent: 15, scope: ["all"], note: "", require_verification: true }))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const allSelected = cfg?.scope?.includes("all");

  const toggleScope = (key) => {
    if (key === "all") { set("scope", ["all"]); return; }
    let next = (cfg.scope || []).filter((s) => s !== "all");
    next = next.includes(key) ? next.filter((s) => s !== key) : [...next, key];
    set("scope", next.length ? next : ["all"]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/veteran-discount", {
        enabled: !!cfg.enabled, code: (cfg.code || "").trim().toUpperCase(),
        percent: Number(cfg.percent) || 0, scope: cfg.scope || ["all"], note: (cfg.note || "").trim(),
        require_verification: !!cfg.require_verification,
        start_date: cfg.start_date || "", end_date: cfg.end_date || "",
      });
      setCfg({ ...data, scope: data.scope || ["all"] });
      toast.success("Veteran discount saved.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-8 border border-white/10 bg-navy-800 max-w-3xl" data-testid="veteran-discount-settings">
      <div className="px-6 py-4 border-b border-white/10">
        <div className="font-heading text-white text-lg">Veteran discount</div>
        <div className="text-xs text-gray-400">You control it like an online store — set the code, the percentage off, and which offers it applies to. When a veteran enters the code at checkout, the price drops automatically.</div>
      </div>
      <div className="p-6">
        {loading || !cfg ? (
          <div className="text-gray-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
        ) : (
          <div className="space-y-5">
            <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
              <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="vd-enabled" className="accent-gold" />
              Discount is live (shows the banner &amp; works at checkout)
            </label>
            <label className="flex items-start gap-2 text-sm text-gray-200 cursor-pointer">
              <input type="checkbox" checked={!!cfg.require_verification} onChange={(e) => set("require_verification", e.target.checked)} data-testid="vd-require-verification" className="accent-gold mt-0.5" />
              <span>Require a quick service verification (recommended — the public code stays hidden and each veteran gets a personal, single-use code so it can't be shared online)</span>
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">Discount code</label>
                <input value={cfg.code || ""} onChange={(e) => set("code", e.target.value.toUpperCase())} data-testid="vd-code"
                  placeholder="e.g. SALUTE15"
                  className="w-full bg-navy border border-white/20 text-white px-3 py-2.5 text-sm uppercase focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">Percent off (%)</label>
                <input type="number" min="0" max="100" value={cfg.percent ?? 0} onChange={(e) => set("percent", e.target.value)} data-testid="vd-percent"
                  className="w-full bg-navy border border-white/20 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-400 block mb-2">Applies to</label>
              <div className="space-y-2">
                {SCOPE_OPTS.map(([key, label]) => {
                  const checked = key === "all" ? allSelected : (!allSelected && cfg.scope?.includes(key));
                  return (
                    <label key={key} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="checkbox" checked={!!checked} onChange={() => toggleScope(key)} data-testid={`vd-scope-${key}`} className="accent-gold" />
                      {label}
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">Banner note (optional)</label>
              <input value={cfg.note || ""} onChange={(e) => set("note", e.target.value)} data-testid="vd-note"
                placeholder='e.g. "on any website package"'
                className="w-full bg-navy border border-white/20 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">Campaign start (optional)</label>
                <input type="date" value={cfg.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="vd-start-date"
                  className="w-full bg-navy border border-white/20 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 block mb-1.5">Campaign end (optional)</label>
                <input type="date" value={cfg.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="vd-end-date"
                  className="w-full bg-navy border border-white/20 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
              </div>
              <p className="sm:col-span-2 text-xs text-gray-500 -mt-1">Leave dates blank to run the discount indefinitely. Outside the window the banner hides and codes stop working automatically.</p>
            </div>
            <button onClick={save} disabled={busy} data-testid="vd-save"
              className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={14} /> : null} Save discount
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PurgeOrphans() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/maintenance/orphan-counts")
      .then((r) => setPlan(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const execute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/purge-orphans", { confirm: "PURGE", dry_run: false });
      setResult(data);
      setConfirmText("");
      toast.success(`Purged ${data.purged_total} orphaned record(s). Backup: ${data.backup_prefix}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const rows = plan ? Object.entries(plan.orphans || {}).sort((a, b) => b[1] - a[1]) : [];
  const total = plan?.orphans_total || 0;

  return (
    <div className="mt-8 border border-amber-500/30 bg-amber-500/[0.04] max-w-3xl" data-testid="purge-orphans-panel">
      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-amber-500/20">
        <AlertTriangle className="text-amber-400" size={18} />
        <div>
          <div className="font-heading text-white text-lg">Clean up orphaned records</div>
          <div className="text-xs text-gray-400">Remove invoices, proposals, messages & files whose parent project was deleted. These inflate dashboard counts but lead to empty drill-downs. Your live projects and their data are untouched.</div>
        </div>
      </div>
      <div className="p-6 space-y-5">
        {loading ? (
          <div className="text-gray-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Scanning for orphaned records…</div>
        ) : (
          <>
            <div className="bg-navy border border-white/10 p-4">
              <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-2 flex items-center gap-1.5"><Trash2 size={12} /> Orphaned (safe to purge)</div>
              <div className="text-3xl font-heading text-white">{total}</div>
              <div className="text-xs text-gray-500">records across {rows.length} collection{rows.length === 1 ? "" : "s"}</div>
              <div className="mt-3 max-h-44 overflow-y-auto space-y-1 pr-1" data-testid="orphan-breakdown">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex justify-between text-xs text-gray-400"><span>{k}</span><span className="text-gray-300 tabular-nums">{v}</span></div>
                ))}
                {rows.length === 0 && <div className="text-xs text-gray-500">No orphaned records — everything is linked to a live project.</div>}
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 p-3">
              <Archive size={14} className="shrink-0 mt-0.5" />
              <span>Purged records are first auto-archived to a timestamped <code className="text-amber-200">bak_orphan_…</code> backup (restorable in Start Fresh below). Affects the <b>current</b> environment's database only.</span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type PURGE to confirm"
                data-testid="purge-confirm-input" disabled={total === 0}
                className="bg-navy border border-white/20 text-white px-3 py-2 text-sm w-56 focus:border-amber-400 focus:outline-none disabled:opacity-40" />
              <button onClick={execute} disabled={busy || total === 0 || confirmText.trim() !== "PURGE"} data-testid="purge-execute-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-amber-500 text-navy hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />} Purge orphaned records
              </button>
              <button onClick={load} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-gray-400 hover:text-white">
                <RefreshCw size={13} /> Rescan
              </button>
            </div>

            {result && (
              <div className="text-xs text-green-300 bg-green-500/10 border border-green-500/20 p-3" data-testid="purge-result">
                ✓ Purged {result.purged_total} orphaned record(s). Backup archived as <code className="text-green-200">{result.backup_prefix}</code>.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StartFresh() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [backups, setBackups] = useState([]);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreText, setRestoreText] = useState("");
  const [restoreBusy, setRestoreBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/maintenance/preview-counts"),
      api.get("/admin/maintenance/backups"),
    ]).then(([p, b]) => {
      setPlan(p.data);
      setBackups(b.data.backup_sets || []);
    }).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const execute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/reset", { confirm: "RESET", dry_run: false });
      setResult(data);
      setConfirmText("");
      toast.success(`Reset complete — ${data.wiped_total} records cleared. Backup: ${data.backup_prefix}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const restore = async (prefix) => {
    setRestoreBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/restore", { backup_prefix: prefix, confirm: "RESTORE" });
      toast.success(`Restored ${data.restored_total} records + ${data.restored_users} users from ${data.restored_prefix}.`);
      setRestoreTarget(null); setRestoreText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setRestoreBusy(false); }
  };

  const rows = plan ? Object.entries(plan.wipe).sort((a, b) => b[1] - a[1]) : [];

  return (
    <div className="mt-8 border border-red-500/30 bg-red-500/[0.04] max-w-3xl" data-testid="start-fresh-panel">
      <div className="flex items-center gap-2.5 px-6 py-4 border-b border-red-500/20">
        <ShieldAlert className="text-red-400" size={18} />
        <div>
          <div className="font-heading text-white text-lg">Danger Zone — Start Fresh</div>
          <div className="text-xs text-gray-400">Wipe all test/demo data to see a real, clean funnel. Your product catalog, strategists, knowledge base, config and owner account are kept.</div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {loading ? (
          <div className="text-gray-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading current data…</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-navy border border-white/10 p-4">
                <div className="text-[11px] uppercase tracking-wider text-red-300 mb-2 flex items-center gap-1.5"><Trash2 size={12} /> Will be deleted</div>
                <div className="text-3xl font-heading text-white">{plan?.wipe_total || 0}</div>
                <div className="text-xs text-gray-500">records across {rows.length} collections + {plan?.non_admin_users || 0} test user{plan?.non_admin_users === 1 ? "" : "s"}</div>
                <div className="mt-3 max-h-44 overflow-y-auto space-y-1 pr-1" data-testid="wipe-breakdown">
                  {rows.map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs text-gray-400"><span>{k}</span><span className="text-gray-300 tabular-nums">{v}</span></div>
                  ))}
                  {plan?.non_admin_users > 0 && (
                    <div className="flex justify-between text-xs text-gray-400"><span>users (non-owner)</span><span className="text-gray-300 tabular-nums">{plan.non_admin_users}</span></div>
                  )}
                  {rows.length === 0 && plan?.non_admin_users === 0 && <div className="text-xs text-gray-500">Nothing to delete — already clean.</div>}
                </div>
              </div>
              <div className="bg-navy border border-white/10 p-4">
                <div className="text-[11px] uppercase tracking-wider text-green-300 mb-2 flex items-center gap-1.5"><Archive size={12} /> Kept (never deleted)</div>
                <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
                  {(plan?.keep || []).map((k) => (
                    <div key={k} className="text-xs text-gray-400">• {k}</div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 p-3">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>Everything deleted is first auto-archived to a timestamped <code className="text-amber-200">bak_…</code> backup, so this is reversible. This affects the <b>current</b> environment's database only.</span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type RESET to confirm"
                data-testid="reset-confirm-input"
                className="bg-navy border border-white/20 text-white px-3 py-2 text-sm w-56 focus:border-red-400 focus:outline-none" />
              <button onClick={execute} disabled={busy || confirmText.trim() !== "RESET"} data-testid="reset-execute-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />} Wipe test data
              </button>
              <button onClick={load} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-gray-400 hover:text-white">
                <RefreshCw size={13} /> Refresh
              </button>
            </div>

            {result && (
              <div className="text-xs text-green-300 bg-green-500/10 border border-green-500/20 p-3" data-testid="reset-result">
                ✓ Cleared {result.wiped_total} records + {result.deleted_users} users. Backup archived as <code className="text-green-200">{result.backup_prefix}</code> ({result.backed_up?.documents} docs).
              </div>
            )}

            {backups.length > 0 && (
              <div className="pt-3 border-t border-white/10">
                <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Backups ({backups.length})</div>
                <div className="space-y-1.5" data-testid="backup-list">
                  {backups.slice(0, 8).map((b, i) => (
                    <div key={b.prefix}>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span className="font-mono">{b.prefix}{i === 0 && <span className="ml-2 text-gold not-italic">latest</span>}</span>
                        <div className="flex items-center gap-3">
                          <span>{b.collections} collections</span>
                          <button onClick={() => { setRestoreTarget(b.prefix); setRestoreText(""); }} data-testid={`restore-btn-${i}`}
                            className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200">
                            <RotateCcw size={12} /> Restore
                          </button>
                        </div>
                      </div>
                      {restoreTarget === b.prefix && (
                        <div className="mt-2 mb-1 flex flex-wrap items-center gap-2 bg-blue-500/10 border border-blue-500/20 p-2.5" data-testid="restore-confirm">
                          <span className="text-[11px] text-blue-200">Restoring replaces current data with this snapshot. Type RESTORE:</span>
                          <input value={restoreText} onChange={(e) => setRestoreText(e.target.value)} placeholder="RESTORE"
                            data-testid="restore-confirm-input"
                            className="bg-navy border border-white/20 text-white px-2.5 py-1.5 text-xs w-32 focus:border-blue-400 focus:outline-none" />
                          <button onClick={() => restore(b.prefix)} disabled={restoreBusy || restoreText.trim() !== "RESTORE"} data-testid="restore-confirm-btn"
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed">
                            {restoreBusy ? <Loader2 className="animate-spin" size={12} /> : <RotateCcw size={12} />} Confirm restore
                          </button>
                          <button onClick={() => { setRestoreTarget(null); setRestoreText(""); }} className="text-xs text-gray-400 hover:text-white">Cancel</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
